import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";

// uuid v4-ish helper (avoids adding a new dependency)
const newSessionId = () =>
    (window.crypto?.randomUUID?.() ||
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

export default function Quiz({ chapterId, chapterTitle, onClose, onResult }) {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [resetCount, setResetCount] = useState(0);
    const submittedRef = useRef(false);
    const sessionId = useMemo(() => newSessionId(), [chapterId]);

    useEffect(() => {
        api.get(`/chapters/${chapterId}/quiz`)
            .then((r) => {
                setQuestions(r.data.questions);
                setAnswers({});
            })
            .catch(() => toast.error("Could not load quiz"))
            .finally(() => setLoading(false));
    }, [chapterId]);

    // Fire-and-forget logger so the server retains an immutable trail of every
    // anti-cheat trigger for instructor review (counts attached on submit).
    const reportEvent = (eventType) => {
        if (submittedRef.current) return;
        api
            .post(`/chapters/${chapterId}/quiz/integrity-event`, {
                session_id: sessionId,
                event_type: eventType,
            })
            .catch(() => {
                /* network blips must not break the quiz UX */
            });
    };

    // Anti-cheat: reset answers if the student leaves the tab or window
    useEffect(() => {
        if (loading || submittedRef.current) return;

        const triggerReset = (reason, eventType) => {
            if (submittedRef.current) return;
            reportEvent(eventType);
            setAnswers({});
            setResetCount((c) => c + 1);
            toast.error(
                `Quiz reset — ${reason}. Please stay on this tab until you submit.`,
                { duration: 6000 },
            );
        };

        const onVisibility = () => {
            if (document.hidden) {
                triggerReset("you switched tabs or minimised the window", "tab_switch");
            }
        };
        const onBlur = () => {
            if (!document.hasFocus()) {
                triggerReset("you left the quiz window", "window_blur");
            }
        };

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("blur", onBlur);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("blur", onBlur);
        };
    }, [loading, chapterId, sessionId]);

    const blockEvent = (e) => {
        e.preventDefault();
        reportEvent("copy_attempt");
        return false;
    };

    const allAnswered = questions.length > 0 && questions.every((_, i) => answers[i] !== undefined);

    const handleSubmit = async () => {
        setSubmitting(true);
        submittedRef.current = true;
        try {
            const payload = {
                answers: questions.map((_, i) => answers[i]),
                session_id: sessionId,
            };
            const { data } = await api.post(`/chapters/${chapterId}/quiz/submit`, payload);
            onResult(data);
        } catch (e) {
            submittedRef.current = false;
            const detail = e?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : detail?.message || "Submission failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto select-none"
                style={{ userSelect: "none", WebkitUserSelect: "none" }}
                onCopy={blockEvent}
                onCut={blockEvent}
                onPaste={blockEvent}
                onContextMenu={blockEvent}
                onDragStart={blockEvent}
                data-testid="quiz-dialog"
            >
                <DialogHeader>
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                        Quiz
                    </div>
                    <DialogTitle className="font-display text-2xl text-primary">
                        {chapterTitle}
                    </DialogTitle>
                </DialogHeader>

                <div
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200"
                    data-testid="quiz-anticheat-warning"
                >
                    <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>
                        <strong>Honour-code mode.</strong> Copying questions is disabled
                        and every tab switch is logged for instructor review.
                        Switching tabs, minimising the window, or losing focus will
                        immediately reset all your answers.
                        {resetCount > 0 ? (
                            <span
                                className="ml-1 font-semibold"
                                data-testid="quiz-reset-count"
                            >
                                · {resetCount} reset{resetCount === 1 ? "" : "s"} so far.
                            </span>
                        ) : null}
                    </div>
                </div>

                {loading ? (
                    <div className="py-8 text-center text-muted-foreground">Loading…</div>
                ) : (
                    <>
                        <div className="space-y-6 py-4">
                            {questions.map((q, i) => (
                                <div
                                    key={i}
                                    className="pb-5 border-b border-border last:border-0"
                                    data-testid={`quiz-q-${i}`}
                                >
                                    <div className="font-medium text-foreground">
                                        {i + 1}. {q.text}
                                    </div>
                                    <RadioGroup
                                        className="mt-3 space-y-2"
                                        value={answers[i]?.toString() ?? ""}
                                        onValueChange={(v) => setAnswers({ ...answers, [i]: Number(v) })}
                                    >
                                        {q.options.map((opt, oi) => (
                                            <div key={oi} className="flex items-center space-x-2">
                                                <RadioGroupItem
                                                    value={oi.toString()}
                                                    id={`q${i}-o${oi}`}
                                                    data-testid={`quiz-q${i}-option-${oi}`}
                                                />
                                                <Label
                                                    htmlFor={`q${i}-o${oi}`}
                                                    className="cursor-pointer text-foreground/85"
                                                >
                                                    {opt}
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-border">
                            <div className="text-sm text-muted-foreground">
                                {Object.keys(answers).length}/{questions.length} answered
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={onClose} data-testid="quiz-cancel-btn">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!allAnswered || submitting}
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="quiz-submit-btn"
                                >
                                    {submitting ? "Submitting…" : "Submit quiz"}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
